import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, NgZone, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChronosProvider, TypeChrono } from '../../providers/chronos/chronos.provider';
import { LoggerWrapper } from '../../providers/logger/logger-wrapper.service';
import { StorageProvider } from '../../providers/storage/storage';
import { Events } from '../../providers/events/events.provider';
import { ActionEtapeSTI, StiProvider } from '../../providers/sti/sti.provider';
import { ActivitesProvider } from '../../providers/activites/activites';
import { AlertController, IonicModule, Platform } from '@ionic/angular';
import { LoggerFactory } from '../../providers/logger/logger-factory.service';
import { ActionSheetMoreProvider, MoreResult } from '../../providers/chronos/action-sheet-more.provider';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SwiperContainer } from 'swiper/swiper-element';
import { GeoLocalisationProvider } from '../../providers/geolocalisation/geolocation.provider';
import { IChrono } from '../../models/chrono.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'chrono',
  templateUrl: 'chrono.component.html',
  styleUrls: ['./chrono.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA], //For using swiper.js (former ion-slide)
})
export class ChronoComponent implements AfterViewInit, OnInit {
  private destroyRef = inject(DestroyRef);

  /**
   * Liste des chronos actifs
   *
   * @type {any}
   */
  chronos: IChrono[] = [];

  /**
   * Logger
   */
  private logger: LoggerWrapper;

  /**
   * Exposition du type chrono pour la vue
   */
  TypeChrono = TypeChrono;

  realIndex = 0;

  swiperEl: SwiperContainer;

  initialSlideNumber: number = 0;

  /**
   * Constructeur
   *
   * Créé une instance de ChronoComponent.
   *
   * @param {StorageProvider} storageProvider
   * @param {Events} events
   * @param {ChronosProvider} chronosProvider
   * @param stiProvider
   * @param {ActivitesProvider} activitesProvider
   * @param {AlertController} alertCtrl
   * @param {TranslateService} translate
   * @param {Platform} platform
   * @param {NgZone} zone
   * @param {LoggerFactory} loggerFactory
   * @param geoLocalisationProvider
   * @param {ActionSheetMoreProvider} actionSheetMoreProvider
   * @param router
   */
  constructor(
    private storageProvider: StorageProvider,
    private events: Events,
    private chronosProvider: ChronosProvider,
    private stiProvider: StiProvider,
    private activitesProvider: ActivitesProvider,
    private alertCtrl: AlertController,
    private translate: TranslateService,
    private zone: NgZone,
    private loggerFactory: LoggerFactory,
    private geoLocalisationProvider: GeoLocalisationProvider,
    private actionSheetMoreProvider: ActionSheetMoreProvider,
    private router: Router
  ) {
    this.logger = this.loggerFactory.getInstance(this);
    this.chronos = this.chronosProvider.getChronos();
    this.events.subscribe('refreshTab', () => {
      this.zone.run(() => {
        this.chronos = this.chronosProvider.getChronos();
        this.swiperEl.swiper.update();
      });
    });

    this.events
      .getObservable('removeChrono')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (id_ot) => {
        const chronoByIdOt = this.chronosProvider.getChronoByIdOt(id_ot);
        await this.removeChrono(id_ot, true, chronoByIdOt?.isStiCreated);
      });
  }

  /**
   * Fonction appelée à l'init du component
   */
  ngOnInit() {
    this.initInitialSlideNumber();
  }

  ngAfterViewInit() {
    this.initSwiper();
    this.slideChanged();
  }

  private initSwiper() {
    this.swiperEl = document.getElementById('slider-chronos') as SwiperContainer;
    const swiperParams = {
      slidesPerView: 1,
      initialSlide: this.initialSlideNumber,
      on: {
        init() {},
      },
    };
    Object.assign(this.swiperEl, swiperParams);
    this.swiperEl.initialize();
  }

  private initInitialSlideNumber() {
    if (this.chronos) {
      this.chronos.find((chrono, index) => {
        if (chrono.active_intervention || chrono.active_travel || chrono.active_return_travel) {
          this.initialSlideNumber = index;
        }
      });
    }
  }

  /**
   * Passe de lecture à pause le chrono selectionné
   *
   * @param {any} ot_id id de l'ot correspondant au chrono
   * @param {any} action type de l'action en cours sur le chrono (travel/intervention)
   */
  async startStop(ot_id, action, id_site) {
    await this.chronosProvider.startStopChrono(ot_id, action, id_site);
    this.chronos = this.chronosProvider.getChronos();
  }

  /**
   * Alerte si le Stop du chrono est appelé depuis un CR IoT. Termine un chrono et renvoi vers le CR selon le choix de l'alerte.
   *
   * @param {any} id_ot id de l'ot correspondant au chrono
   */
  async stop(id_site, id_ot = null) {
    //if (this.navCtrl.getActive().id === 'IotCapteurCrPosePage') {
    if (this.router.url === '/iot/iot-capteur-cr-pose') {
      const alert = await this.alertCtrl.create({
        message: this.translate.instant(
          "Vous souhaitez retourner sur l'OT. Les opérations de pose non finalisées vont être perdues, souhaitez vous poursuivre ?"
        ),
        buttons: [
          {
            text: this.translate.instant('Oui'),
            handler: () => {
              this.stopAndRedirect(id_site, id_ot);
            },
          },
          { text: this.translate.instant('Non') },
        ],
      });
      await alert.present();
    } else if (this.router.url.includes('/mnt/ots/' + id_ot + '/cr')) {
      // on est deja sur la page cr, on disable l'appui sur le bouton stop
      return;
    } else {
      await this.stopAndRedirect(id_site, id_ot);
    }
  }

  /**
   * Termine un chrono et renvoi vers le CR
   *
   * @param {any} id_ot id de l'ot correspondant au chrono
   */
  async stopAndRedirect(id_site, id_ot = null) {
    let activeIndex = this.swiperEl.swiper.realIndex;
    const chrono = this.chronos[activeIndex];
    let ot: any;

    if (chrono.typeChrono === TypeChrono.Ot) {
      await this.chronosProvider.presentLoading();
      // Ot non unplanned
      if (id_ot != null && id_site != id_ot) {
        ot = await this.activitesProvider.getActivity(id_ot, id_site);
      }
    }

    let cr = await this.chronosProvider.stopChrono(chrono);
    if (!id_site && ot) {
      cr.operations = ot.operations || [];
    }

    this.chronos.splice(activeIndex, 1);

    await this.storageProvider.set('chronos', this.chronos, false);
    this.swiperEl.swiper.slidePrev();

    if (ot != null) {
      this.events.publish('saveOt', id_ot);
      let params = {
        irId: id_ot,
        siteId: id_site,
        chrono: cr,
      };
      setTimeout(() => {
        this.chronosProvider.redirectToCr(params);
      }, 500);
    } else {
      setTimeout(() => {
        if (chrono.typeChrono === TypeChrono.ActiviteInterne) {
          this.router.navigate(['/mnt/activite-interne'], {
            state: { cr: cr, editable: true },
          });
        } else {
          this.router.navigate(['/mnt/unplanned-ot'], {
            state: { site: { id: id_site }, cr: cr },
          });
        }
      }, 500);
    }
    this.events.publish('refreshTab', Date.now());
  }

  /**
   * Supprime un chrono
   *
   * @param {any} id_ot id de l'ot correspondant au chrono
   * @param {boolean} isStiCreated le chrono est-il associé à un sti créé
   */
  async confirmRemoveChrono(id_ot: string, isStiCreated: boolean) {
    const messageKey = isStiCreated ? 'CHRONO.SUPPR_CHRONO_STI' : 'CHRONO.SUPPR_CHRONO';

    let alert = await this.alertCtrl.create({
      cssClass: 'alertButtons',
      header: '',
      message: this.translate.instant(messageKey),
      buttons: [
        {
          text: this.translate.instant('GLOBAL.NO'),
        },
        {
          text: this.translate.instant('GLOBAL.YES'),
          handler: () => {
            this.removeChrono(id_ot, true, isStiCreated);
          },
        },
      ],
    });

    await alert.present();
  }

  /**
   * Click sur le chrono
   */
  async clickChronoOt(chrono) {
    if (chrono.activeChrono) this.goToDetailOt(chrono.idOt, chrono.id_site, chrono.unplanned);
    else {
      this.changeSlide();
    }
  }

  /**
   * Supprime le chrono
   * @param id_ot identifiant de l'ordre de travail
   * @param withWaiting supprimer le chrono des chronos en attente
   */
  async removeChrono(id_ot: string, withWaiting: boolean, isStiCreated = false) {
    let activeIndex = this.swiperEl.swiper.realIndex;
    if (isStiCreated) {
      await this.stiProvider.actionSTI(this.chronos[activeIndex].idSti, ActionEtapeSTI.CLOTURE_ETAPE);
      await this.geoLocalisationProvider.stopGeolocation(id_ot);
      this.events.publish('refreshOt');
    }

    if (this.chronos.length > 1) {
      this.chronos.splice(activeIndex, 1);
      await this.storageProvider.set('chronos', this.chronos, false);
      this.swiperEl.swiper.slidePrev();
      this.events.publish('refreshTab', Date.now());
      this.events.publish('supprChrono', id_ot);
    } else {
      await this.storageProvider.remove('chronos');
      this.chronos = null;
      this.chronosProvider.deleteChronos();
      this.events.publish('refreshTab', Date.now());
      this.events.publish('supprChrono', id_ot);
    }

    if (withWaiting) {
      await this.storageProvider.removeDraft({ id: id_ot }, 'chronoWaiting');
    }
  }

  /**
   * Renvoi vers la prochaine slide du carousel
   *
   */
  changeSlide() {
    setTimeout(() => {
      if (this.swiperEl && this.swiperEl.swiper.realIndex == 0) this.swiperEl.swiper.slideNext();
      else this.swiperEl.swiper.slidePrev();
    }, 300);
  }

  /**
   * Fonction déclenchée au changement de slide
   */
  slideChanged() {
    console.log('this.chronos', this.chronos);
    // let activeIndex;
    if (this.swiperEl && this.swiperEl.swiper.activeIndex >= 0) {
      this.realIndex = this.swiperEl.swiper.activeIndex;
    }
  }

  /**
   * Accéder au détail de l'OT
   * @param id_ot identifiant de l'ordre de travail
   * @param id_site identifiant du site
   * @param unplanned précise si l'OT est planifiée ou non
   */
  goToDetailOt(id_ot, id_site, unplanned) {
    let activeIndex = this.swiperEl.swiper.realIndex;

    if (this.chronos[activeIndex].idOt === id_ot && this.chronos[activeIndex].libelleOt) {
      if (this.chronos[activeIndex].typeChrono === TypeChrono.ActiviteInterne) {
        return;
      }

      this.router.navigate(['/mnt/ots/', id_ot], {
        queryParams: {
          siteId: id_site,
        },
      });
    } else if (
      this.chronos[activeIndex].id_site === id_site &&
      this.chronos[activeIndex].idOt === id_site &&
      unplanned
    ) {
      this.router.navigate(['/mnt/sites/', id_site]);
    } else {
      this.changeSlide();
    }
  }

  /**
   * Click sur le bouton ...
   */
  async onMoreClick(event: Event, chrono: IChrono) {
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    try {
      const result: MoreResult = await this.actionSheetMoreProvider.openActionSheetMore(
        chrono.isStiCreated,
        chrono.canalCreationSti,
        chrono.isSiu,
        chrono.unplanned,
        chrono.typeChrono === TypeChrono.Ot
      );

      switch (result) {
        case MoreResult.CLOSE:
          await this.stop(chrono.id_site, chrono.idOt);
          break;
        case MoreResult.DETAIL:
          await this.router.navigate(['/mnt/stis/', chrono.idSti, 'details'], {
            queryParams: {
              idSti: chrono.idSti,
              libelleOt: chrono.libelleOt,
            },
          });
          break;
        case MoreResult.REMOVE_CHRONO:
          await this.confirmRemoveChrono(chrono.idOt, chrono.isStiCreated);
          break;
      }
    } catch (error) {
      this.logger.error(error, 'Error on onMoreClick');
    }
  }
}
